'use client';

import { Button, Select } from '@nerdwallet/currency';
import { MouseEvent } from 'react';
import styles from '../auto-insurance-matches.module.scss';
import { AGE_OPTIONS, AUTO_INSURERS, TOGGLE_OPTIONS } from '../constants';
import { formatCamelCase } from '../utils';
import MatchesFiltersChoices from './matches-filters-choices';

export type MatchesFiltersProps = {
  filters: {
    age: string;
    insurer: string;
    isMarried: boolean;
    isHomeOwner: boolean;
    isVeteran: boolean;
    addHomeInsurance: boolean;
  };
  updateFilter: <T extends string | boolean>(
    key: keyof MatchesFiltersProps['filters'],
    value: T
  ) => void;
};

const AgeSelector = ({
  age,
  updateFilter,
}: {
  age: string;
  updateFilter: MatchesFiltersProps['updateFilter'];
}) =>
  !age && (
    <div className={`${styles.selectorContainer} ${styles.age}`}>
      <span className={styles.selectorLabel}>Age</span>
      <div>
        <Select
          id="age"
          value={age}
          onChange={(e: { target: { value: any } }) =>
            updateFilter('age', e.target.value)
          }
          className={styles.selectInput}
          options={[{ label: 'Age', value: '' }, ...AGE_OPTIONS]}
        />
      </div>
    </div>
  );

const InsurerSelector = ({
  insurer,
  updateFilter,
}: {
  insurer: string;
  updateFilter: MatchesFiltersProps['updateFilter'];
}) =>
  !insurer && (
    <div className={`${styles.selectorContainer} ${styles.insurer}`}>
      <span className={styles.selectorLabel}>Current Insurer</span>
      <div>
        <Select
          id="insurer"
          value={insurer}
          onChange={(e: { target: { value: any } }) =>
            updateFilter('insurer', e.target.value)
          }
          className={styles.selectInput}
          options={[{ label: 'Current Insurer', value: '' }, ...AUTO_INSURERS]}
        />
      </div>
    </div>
  );

const ToggleOptionsGroup = ({
  activeToggles,
  handleToggle,
}: {
  activeToggles: string[];
  handleToggle: (event: MouseEvent<HTMLElement>, newValue: string) => void;
}) => (
  <div className={styles.toggleOptionsContainer}>
    {TOGGLE_OPTIONS.filter((option) => !activeToggles.includes(option)).map(
      (option) => (
        <Button
          tertiary
          key={option}
          onClick={(e) => handleToggle(e, option)}
          className={styles.toggleButton}
        >
          <span className={styles.toggleButtonText}>
            {formatCamelCase(option)}
          </span>
        </Button>
      )
    )}
  </div>
);

const MatchesFilters = ({ filters, updateFilter }: MatchesFiltersProps) => {
  const { age, insurer, isMarried, isHomeOwner, isVeteran, addHomeInsurance } =
    filters;

  const activeToggles: string[] = [];
  if (isMarried) activeToggles.push('married');
  if (isHomeOwner) activeToggles.push('homeowner');
  if (isVeteran) activeToggles.push('veteran');
  if (addHomeInsurance) activeToggles.push('addHomeInsurance');

  const handleToggle = (event: MouseEvent<HTMLElement>, newValue: string) => {
    const updatedToggles = activeToggles.includes(newValue)
      ? activeToggles.filter((value) => value !== newValue)
      : [...activeToggles, newValue];

    updateFilter('isMarried', updatedToggles.includes('married'));
    updateFilter('isHomeOwner', updatedToggles.includes('homeowner'));
    updateFilter('isVeteran', updatedToggles.includes('veteran'));
    updateFilter(
      'addHomeInsurance',
      updatedToggles.includes('addHomeInsurance')
    );
  };

  return (
    <div className={styles.filtersWrapper}>
      <MatchesFiltersChoices filters={filters} updateFilter={updateFilter} />
      <div className={styles.filtersRow}>
        <AgeSelector age={age} updateFilter={updateFilter} />
        <InsurerSelector insurer={insurer} updateFilter={updateFilter} />
      </div>
      <ToggleOptionsGroup
        activeToggles={activeToggles}
        handleToggle={handleToggle}
      />
    </div>
  );
};

export default MatchesFilters;
